import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Headphones, Calculator, TrendingUp, Target, Trophy } from 'lucide-react';
import { QuoteForm } from './QuoteForm';
import { PackageSelector } from './PackageSelector';
import { DiscountSelector } from './DiscountSelector';
import { Input } from './ui/input';
import { Label } from './ui/label';

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors"
        data-testid="btn-toggle-audiobook"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 bg-[#aa2aff] text-white text-xs rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-[#080c2d]/50">
          {children}
        </div>
      )}
    </div>
  );
}

export function AudiobookSection({
  isOpen,
  onToggle,
  formData,
  onFormChange,
  selectedPackage,
  onPackageSelect,
  packages,
  formatPrice,
  currency,
  exchangeRate = 17.5,
  manualDiscount,
  volumeDiscount,
  manualDiscounts,
  durationHours,
  onManualDiscountChange,
  showDiscounts,
  quote,
  pvp = 0,
  onPvpChange,
  audiobookSubtotal = 0,
  salesGoal = 0,
  onSalesGoalChange,
  onShowPackageDetails,
  voiceCloning = false,
  onVoiceCloningChange,
  talentoPremium = false,
  onTalentoPremiumChange,
  talentoPremiumPricePerHour = 1500,
  extraVoicesUltra = 0,
  onExtraVoicesUltraChange,
  revisionAudio = 'none',
  onRevisionAudioChange,
  masterizacion = 'none',
  onMasterizacionChange,
  multivozActive = false,
  onMultivozActiveChange,
  multivozVoces = 0,
  onMultivozVocesChange
}) {
  const [rentabilidadOpen, setRentabilidadOpen] = useState(false);
  const badge = quote?.grandTotal ? formatPrice(quote.grandTotal) : null;
  
  // Calculate break-even point (Punto de Equilibrio)
  // PVP is entered in the display currency, audiobookSubtotal is always in MXN
  // Convert audiobookSubtotal to the display currency for consistent calculation
  const royaltyMultiplier = 0.57;
  const effectiveSubtotal = currency === 'USD' ? audiobookSubtotal / exchangeRate : audiobookSubtotal;
  const revenuePerUnit = pvp * royaltyMultiplier;
  const breakEvenUnits = revenuePerUnit > 0 ? Math.ceil(effectiveSubtotal / revenuePerUnit) : 0;
  
  // Calculate ROI based on sales goal
  const roi = breakEvenUnits > 0 && salesGoal > 0 ? (salesGoal / breakEvenUnits).toFixed(1) : 0;

  // Format values that are ALREADY in the display currency (don't use formatPrice which converts again)
  const formatDirectPrice = (val) => {
    if (!val || val === 0) return '$0';
    return `$${Math.round(val).toLocaleString()}`;
  };

  return (
    <CollapsibleSection
      title="Audiolibros"
      icon={Headphones}
      isOpen={isOpen}
      onToggle={onToggle}
      badge={badge}
    >
      <div className="space-y-6">
        {/* Book Details */}
        <div className="bg-[#0f133a]/40 rounded-xl p-4 border border-white/5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Headphones className="w-4 h-4 text-[#aa2aff]" />
            Datos del Libro
          </h3>
          <QuoteForm 
            formData={formData} 
            onChange={onFormChange}
            calculatedDuration={quote?.estimatedDurationHours || 0}
          />
        </div>

        {/* Package Selection */}
        <div className="bg-[#0f133a]/40 rounded-xl p-4 border border-white/5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Selecciona tu Paquete</h3>
            {onShowPackageDetails && (
              <button
                onClick={onShowPackageDetails}
                className="text-[#aa2aff] text-xs font-medium hover:text-white transition-colors underline underline-offset-2"
                data-testid="btn-show-package-details"
              >
                Ver detalles de cada paquete
              </button>
            )}
          </div>
          <PackageSelector
            packages={packages}
            selectedPackage={selectedPackage}
            onSelect={onPackageSelect}
            formatPrice={formatPrice}
            currency={currency}
          />
        </div>

        {/* Voice Cloning checkbox - only for Ultra package */}
        {selectedPackage === 'ultra' && (
          <div className="bg-gradient-to-r from-cyan-500/10 to-teal-500/10 rounded-xl p-4 border border-cyan-500/30 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="label-voice-cloning">
              <input
                type="checkbox"
                checked={voiceCloning}
                onChange={(e) => onVoiceCloningChange(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-cyan-500 text-cyan-500 focus:ring-cyan-500 accent-cyan-500"
                data-testid="checkbox-voice-cloning"
              />
              <div>
                <p className="text-white font-semibold text-sm">
                  Incluir Clonacion de Voz Original
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  1 sesion de grabacion original para registro de voz y clonacion autorizada de 3-4 horas
                </p>
                <p className="text-cyan-400 font-bold text-sm mt-1">
                  +{formatPrice(7997)} {currency} + IVA (pago unico)
                </p>
              </div>
            </label>

            {/* Extra cloned voices - Ultra only */}
            <div className="border-t border-cyan-500/20 pt-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">Voces Clonadas Adicionales</p>
                  <p className="text-slate-400 text-xs">{formatPrice(697)} {currency} por cada voz adicional</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onExtraVoicesUltraChange(Math.max(0, extraVoicesUltra - 1))}
                    className="w-7 h-7 rounded-lg bg-[#080c2d] border border-cyan-500/30 text-cyan-400 flex items-center justify-center hover:bg-cyan-500/10"
                    data-testid="btn-ultra-voices-minus"
                  >-</button>
                  <span className="text-white font-bold w-6 text-center" data-testid="ultra-voices-count">{extraVoicesUltra}</span>
                  <button
                    onClick={() => onExtraVoicesUltraChange(extraVoicesUltra + 1)}
                    className="w-7 h-7 rounded-lg bg-[#080c2d] border border-cyan-500/30 text-cyan-400 flex items-center justify-center hover:bg-cyan-500/10"
                    data-testid="btn-ultra-voices-plus"
                  >+</button>
                </div>
              </div>
              {extraVoicesUltra > 0 && (
                <p className="text-cyan-400 text-xs mt-1 text-right">+{formatPrice(extraVoicesUltra * 697)} {currency}</p>
              )}
            </div>
          </div>
        )}

        {/* Multivoz Module - available for all packages */}
        {selectedPackage && (
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30 overflow-hidden">
            <label className="flex items-start gap-3 cursor-pointer p-4" data-testid="label-multivoz">
              <input
                type="checkbox"
                checked={multivozActive}
                onChange={(e) => onMultivozActiveChange(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-purple-500 text-purple-500 focus:ring-purple-500 accent-purple-500"
                data-testid="checkbox-multivoz"
              />
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">Multivoz</p>
                <p className="text-slate-400 text-xs mt-1">Agrega voces adicionales a tu producción</p>
                <p className="text-purple-400 font-bold text-xs mt-1">
                  {formatPrice(997)} {currency} Booking & Management Talent Fees + {formatPrice(50)} {currency}/min final narrado
                </p>
              </div>
            </label>
            {multivozActive && (
              <div className="px-4 pb-4 border-t border-purple-500/20 pt-3">
                <div className="flex items-center justify-between">
                  <p className="text-slate-300 text-sm">Cantidad de voces adicionales</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onMultivozVocesChange(Math.max(1, multivozVoces - 1))}
                      className="w-7 h-7 rounded-lg bg-[#080c2d] border border-purple-500/30 text-purple-400 flex items-center justify-center hover:bg-purple-500/10"
                      data-testid="btn-multivoz-minus"
                    >-</button>
                    <span className="text-white font-bold w-6 text-center" data-testid="multivoz-count">{multivozVoces}</span>
                    <button
                      onClick={() => onMultivozVocesChange(multivozVoces + 1)}
                      className="w-7 h-7 rounded-lg bg-[#080c2d] border border-purple-500/30 text-purple-400 flex items-center justify-center hover:bg-purple-500/10"
                      data-testid="btn-multivoz-plus"
                    >+</button>
                  </div>
                </div>
                <p className="text-purple-400 text-xs mt-2 text-right font-medium">
                  {formatPrice(997)} + {multivozVoces} voz(es) x {formatPrice(50)}/min x {((durationHours || 0) * 60).toFixed(0)} min = {formatPrice(997 + (multivozVoces * 50 * (durationHours || 0) * 60))} {currency}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Talento Premium - available for all packages */}
        {selectedPackage && (
          <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl border border-amber-500/30 overflow-hidden">
            <label className="flex items-start gap-3 cursor-pointer p-4" data-testid="label-talento-premium">
              <input
                type="checkbox"
                checked={talentoPremium}
                onChange={(e) => onTalentoPremiumChange(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-amber-500 text-amber-500 focus:ring-amber-500 accent-amber-500"
                data-testid="checkbox-talento-premium"
              />
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">Upgrade a Talento Premium</p>
                <p className="text-slate-400 text-xs mt-1">Talento de Voz con más de 10 audiolibros verificables en Audible</p>
                <p className="text-amber-400 font-bold text-xs mt-1">
                  +{formatPrice(talentoPremiumPricePerHour)} {currency} por hora terminada
                  {durationHours > 0 && talentoPremium && (
                    <span className="text-amber-300 ml-2">
                      = {formatPrice(talentoPremiumPricePerHour * durationHours)} {currency}
                    </span>
                  )}
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Discounts - Only show if enabled */}
        {showDiscounts && (
          <div className="bg-[#0f133a]/40 rounded-xl p-4 border border-white/5">
            <h3 className="text-white font-semibold mb-4">Descuentos</h3>
            <DiscountSelector
              manualDiscount={manualDiscount}
              volumeDiscount={volumeDiscount}
              availableDiscounts={manualDiscounts}
              durationHours={durationHours}
              onManualDiscountChange={onManualDiscountChange}
            />
          </div>
        )}

        {/* PVP y Punto de Equilibrio - Collapsible */}
        <div className="bg-gradient-to-br from-[#0f133a]/60 to-[#1a1f50]/60 rounded-xl border border-[#aa2aff]/30 overflow-hidden">
          <button
            onClick={() => setRentabilidadOpen(prev => !prev)}
            className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            data-testid="btn-toggle-rentabilidad"
          >
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Calculator className="w-4 h-4 text-[#aa2aff]" />
              Análisis de Rentabilidad
            </h3>
            {rentabilidadOpen ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>
          
          {rentabilidadOpen && (
          <div className="px-4 pb-4 space-y-4">
            {/* PVP Input */}
            <div className="space-y-2">
              <Label htmlFor="pvp" className="text-slate-300 flex items-center gap-2">
                Precio de Venta al Público (PVP)
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">$</span>
                <Input
                  id="pvp"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Ej: 199"
                  value={pvp || ''}
                  onChange={(e) => onPvpChange(parseFloat(e.target.value) || 0)}
                  className="bg-[#080c2d] border-[#272b96] text-white w-32 focus:border-[#aa2aff]"
                  data-testid="input-pvp"
                />
                <span className="text-slate-400">{currency}</span>
              </div>
              <p className="text-xs text-slate-500">
                El precio al que venderás tu audiolibro al público
              </p>
            </div>

            {/* Punto de Equilibrio Result */}
            {pvp > 0 && audiobookSubtotal > 0 && (
              <div className="bg-[#080c2d]/80 rounded-lg p-4 border border-[#aa2aff]/20">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  <span className="text-white font-semibold">Punto de Equilibrio</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Inversión del Audiolibro:</p>
                    <p className="text-white font-medium">{formatPrice(audiobookSubtotal)} {currency}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">PVP × 57% (regalía):</p>
                    <p className="text-white font-medium">{formatDirectPrice(revenuePerUnit)} {currency}/venta</p>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-gradient-to-r from-green-500/10 to-[#aa2aff]/10 rounded-lg border border-green-500/30">
                  <p className="text-slate-300 text-sm">Para recuperar tu inversión necesitas vender:</p>
                  <p className="text-3xl font-bold text-green-400 mt-1">
                    {breakEvenUnits.toLocaleString()} unidades
                  </p>
                </div>

                {/* Sales Goal Input */}
                <div className="mt-4 space-y-2">
                  <Label htmlFor="salesGoal" className="text-slate-300 flex items-center gap-2">
                    <Target className="w-4 h-4 text-[#aa2aff]" />
                    ¿Cuántas copias de audiolibro te gustaría vender?
                  </Label>
                  <Input
                    id="salesGoal"
                    type="number"
                    min="0"
                    placeholder="Ej: 500"
                    value={salesGoal || ''}
                    onChange={(e) => onSalesGoalChange(parseInt(e.target.value) || 0)}
                    className="bg-[#080c2d] border-[#272b96] text-white w-40 focus:border-[#aa2aff]"
                    data-testid="input-sales-goal"
                  />
                </div>

                {/* ROI Result */}
                {salesGoal > 0 && salesGoal >= breakEvenUnits && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-yellow-500/10 to-green-500/10 rounded-lg border border-yellow-500/30 animate-in fade-in duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-6 h-6 text-yellow-400" />
                      <span className="text-yellow-400 font-bold">¡Felicidades!</span>
                    </div>
                    <p className="text-white">
                      Con esta meta tu <span className="text-[#aa2aff] font-semibold">RDI (ROI)</span> sería de{' '}
                      <span className="text-2xl font-bold text-green-400">{roi}</span>{' '}
                      veces sobre el valor de tu inversión.
                    </p>
                    <p className="text-green-300 mt-2">
                      Generarías aproximadamente{' '}
                      <span className="font-bold text-white text-lg">
                        {formatDirectPrice(revenuePerUnit * salesGoal)} {currency}
                      </span>
                    </p>
                  </div>
                )}

                {salesGoal > 0 && salesGoal < breakEvenUnits && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-lg border border-orange-500/30 animate-in fade-in duration-300">
                    <p className="text-orange-300 text-sm">
                      Con {salesGoal.toLocaleString()} ventas aún no recuperas tu inversión. 
                      Necesitas al menos <span className="font-bold text-white">{breakEvenUnits.toLocaleString()}</span> ventas para alcanzar el punto de equilibrio.
                    </p>
                  </div>
                )}
                
                <p className="text-xs text-slate-500 mt-3">
                  * Basado en una regalía del 57% sobre el precio de venta
                </p>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
}

export default AudiobookSection;
